<template>
  <div>
    <TopBar
      :title="topBarTitle"
      @go-back="goBack"
      :show-back-button="!manufacturingOrder"
      :show-toggle="true"
      :prevent-go-back="preventGoBack"
      class="mb-8"
    />

    <ScanOp v-if="currentScreen === MoScreen.SCAN_OP" />

    <ExceptionView v-if="currentScreen === MoScreen.EXCEPTION" type="ended-op" />

    <MoInfo
      v-if="currentScreen === MoScreen.MO_INFO"
      :handle-change-mo-click="handleChangeMoClick"
      :handle-click-load-product="handleClickLoadProduct"
      :mo-product-card-data="moProductCardData"
    />

    <ProductDetails v-if="currentScreen === MoScreen.PRODUCT_DETAILS" />
  </div>
</template>

<script setup lang="ts">
import MoInfo from '@/components/MoInfo/MoInfo.vue'
import ScanOp from '@/components/ScanOp/ScanOp.vue'
import TopBar from '@/components/TopBar/TopBar.vue'
import { useEventBus } from '@/plugins/eventBus/eventBus'
import { useAppStore } from '@/stores/app.store'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useFetchManufacturingOrder } from '../../components/ScanOp/query'
import ExceptionView from '../Exception/ExceptionView.vue'
import ProductDetails from '../ProductDetails/ProductDetails.vue'
import { MoScreen, useInternalRouter } from './hooks/useInternalRouter'

const eventBus = useEventBus()

const appStore = useAppStore()

const { manufacturingOrder, currentScreen, goBack, isScanningTag, preventGoBack } =
  useInternalRouter()

const moCode = computed(() => appStore.currentManufacturingOrder?.orderNumber.toString() ?? '')

const { data, refetch } = useFetchManufacturingOrder(moCode)

const moProductCardData = computed(() =>
  appStore.currentManufacturingOrder
    ? {
        imageUrl: appStore.currentManufacturingOrder.imageUrl,
        productUnitCode: appStore.currentManufacturingOrder.productUnitCode,
        primaryColorHex: appStore.currentManufacturingOrder.primaryColorHex,
        secondaryColorHex: appStore.currentManufacturingOrder.secondaryColorHex,
        description: appStore.currentManufacturingOrder.description,
        size: appStore.currentManufacturingOrder.size,
        year: appStore.currentManufacturingOrder.year,
        checked: appStore.currentManufacturingOrder.checked,
        problem: appStore.currentManufacturingOrder.problem,
        total: appStore.currentManufacturingOrder.total,
        loadedAt: appStore.currentMoProduct?.loadedAt
      }
    : null
)

const topBarTitle = computed(() =>
  manufacturingOrder.value?.orderNumber
    ? appStore.nonConformityView
      ? `TAG ${appStore.currentTag}`
      : `OP ${manufacturingOrder.value.orderNumber}`
    : null
)

appStore.actions.topBar.setTopBarTitle(topBarTitle.value)

async function handleClickLoadProduct() {
  isScanningTag.value = true
}

async function refetchManufacturingOrder() {
  if (moCode.value) {
    await refetch()

    if (data.value) {
      appStore.actions.setManufacturingOrder(data.value)
    }
  }
}

watch(data, async () => {
  if (data.value) {
    appStore.actions.setManufacturingOrder(data.value)
  }
})

watch(isScanningTag, async () => {
  if (!isScanningTag.value) {
    eventBus.on('moDetails:loadProduct', handleClickLoadProduct)
  }
})

onMounted(async () => {
  eventBus.on('moDetails:loadProduct', handleClickLoadProduct)
  eventBus.on('moDetails:changeOp', handleChangeMoClick)

  await refetchManufacturingOrder()
})

onUnmounted(async () => {
  eventBus.off('moDetails:loadProduct')
  eventBus.off('moDetails:changeOp')
})

async function handleChangeMoClick() {
  appStore.actions.setManufacturingOrder(null)
}
</script>
